import React from 'react';
import { Calendar, MapPin } from 'lucide-react';
import type { TransparencyProject } from '@yawi/types';
import { TransparencyProjectStatus } from '@yawi/types';

const STATUS_LABEL: Record<TransparencyProjectStatus, string> = {
  PLANNING: 'Planeación',
  APPROVED: 'Aprobado',
  IN_PROGRESS: 'En progreso',
  PAUSED: 'Pausado',
  DELAYED: 'Retrasado',
  COMPLETED: 'Completado',
  CANCELLED: 'Cancelado',
};

const STATUS_COLOR: Record<TransparencyProjectStatus, string> = {
  PLANNING: 'bg-slate-100 text-slate-600',
  APPROVED: 'bg-cyan-100 text-cyan-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  DELAYED: 'bg-orange-100 text-orange-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

interface ProjectCardProps {
  project: TransparencyProject;
  onClick: (project: TransparencyProject) => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  return (
    <button
      onClick={() => onClick(project)}
      className="text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
    >
      {project.coverImageUrl && (
        <img src={project.coverImageUrl} alt={project.title} className="w-full h-36 object-cover" />
      )}
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[project.status]}`}>
            {STATUS_LABEL[project.status]}
          </span>
          <span className="text-xs text-slate-400">{project.progress}%</span>
        </div>
        <h3 className="font-semibold text-slate-800 line-clamp-1">{project.title}</h3>
        {project.shortDescription && (
          <p className="text-sm text-slate-500 line-clamp-2">{project.shortDescription}</p>
        )}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-primary-500" style={{ width: `${project.progress}%` }} />
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400 pt-1">
          {project.communityLocation && (
            <span className="flex items-center gap-1">
              <MapPin size={12} /> {project.communityLocation}
            </span>
          )}
          {project.estimatedCompletionDate && (
            <span className="flex items-center gap-1">
              <Calendar size={12} /> {project.estimatedCompletionDate}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};
